import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { delegationsI18n } from './i18n';
import { fetchPagedTasks } from '../../../../services/TaskService';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';

const DEFAULT_TRANSFERS = {
  cases: true,
  tasks: true,
  events: true,
};

export default function DelegationCreateEditModal({
  Modal,
  Input,
  Dropdown,
  ComboBox,
  Checkbox,
  show,
  mode = 'create',
  initialData = null,
  userOptions = [],
  reasonOptions = [],
  statusOptions = [],
  defaultStatusId = null,
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const delegationsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: delegationsI18n });

  const isEdit = mode === 'edit';

  useEffect(() => {
    delegationsI18n.changeLanguage(delegationsLanguage);
  }, [delegationsLanguage]);

  const typeOptions = useMemo(
    () => [
      { label: t('delegations_modal_type_temporary'), value: 1 },
      { label: t('delegations_modal_type_permanent'), value: 2 },
    ],
    [t]
  );

  const normalizeText = (value) =>
    String(value ?? '')
      .trim()
      .toLowerCase();

  const normalizeTypeValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number') return value;
    if (typeof value === 'boolean') return value ? 2 : 1;

    const normalized = normalizeText(value);
    if (
      normalized === '1' ||
      normalized === 'temporal' ||
      normalized === 'temporary'
    ) {
      return 1;
    }
    if (
      normalized === '2' ||
      normalized === 'permanente' ||
      normalized === 'permanent'
    ) {
      return 2;
    }

    const numeric = Number(value);
    if (!Number.isNaN(numeric)) return numeric;
    return '';
  };

  const isPermanentType = (value) => normalizeTypeValue(value) === 2;

  const [form, setForm] = useState({
    fromUser: null,
    toUser: null,
    startDate: '',
    endDate: '',
    reason: '',
    status: defaultStatusId ?? '',
    type: 1,
    transfers: { ...DEFAULT_TRANSFERS },
  });
  const [saving, setSaving] = useState(false);
  const [showPermanentConfirm, setShowPermanentConfirm] = useState(false);
  const [pendingTaskCount, setPendingTaskCount] = useState(0);

  const fetchTaskCount = useCallback(async (userId) => {
    try {
      const result = await fetchPagedTasks({
        usuarioAsignadoId: userId,
        statusId: 2, // En progreso
        pageNumber: 1,
        pageSize: 1,
      });
      return result?.totalCount ?? 0;
    } catch {
      return 0;
    }
  }, []);

  useEffect(() => {
    if (!show) return;

    const resolveUserId = (idValue, nameValue) => {
      if (idValue !== null && idValue !== undefined && idValue !== '')
        return idValue;
      if (!nameValue) return '';
      const normalized = normalizeText(nameValue);
      const match = userOptions.find(
        (option) => normalizeText(option.label) === normalized
      );
      return match?.value ?? nameValue;
    };

    if (isEdit && initialData) {
      const fromUserResolved = resolveUserId(
        initialData.fromUserId ??
          initialData.usuarioTitularId ??
          initialData.outgoingUserId ??
          initialData.outgoingUser?.id,
        initialData.fromUser ?? initialData.outgoingUserName
      );
      const toUserResolved = resolveUserId(
        initialData.toUserId ??
          initialData.usuarioSuplenteId ??
          initialData.substituteUserId ??
          initialData.substituteUser?.id,
        initialData.toUser ?? initialData.substituteUserName
      );
      const start = initialData.startDate ?? initialData.fechaInicio;
      const end = initialData.endDate ?? initialData.fechaFin;
      const resolvedTypeRaw =
        initialData.typeId ??
        initialData.TypeId ??
        initialData.tipoId ??
        initialData.tipo ??
        initialData.type ??
        initialData.typeName ??
        initialData.tipoNombre ??
        (initialData.isPermanent ? 2 : 1) ??
        1;
      const resolvedStatus =
        initialData.statusId ??
        initialData.status ??
        initialData.estado ??
        initialData.statusName ??
        defaultStatusId ??
        '';

      setForm({
        fromUser: fromUserResolved,
        toUser: toUserResolved,
        startDate: start ? String(start).slice(0, 10) : '',
        endDate: end ? String(end).slice(0, 10) : '',
        reason:
          initialData.reasonId ??
          initialData.motivoId ??
          initialData.reason ??
          initialData.motivo ??
          '',
        status: resolvedStatus,
        type: normalizeTypeValue(resolvedTypeRaw) || 1,
        transfers: {
          ...DEFAULT_TRANSFERS,
          ...(initialData.transfers || {}),
        },
      });
    } else {
      setForm({
        fromUser: '',
        toUser: '',
        startDate: '',
        endDate: '',
        reason: '',
        status: defaultStatusId ?? '',
        type: 1,
        transfers: { ...DEFAULT_TRANSFERS },
      });
    }
  }, [show, isEdit, initialData, userOptions, defaultStatusId]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setTransfer = (key, value) =>
    setForm((prev) => ({
      ...prev,
      transfers: { ...prev.transfers, [key]: value },
    }));

  const resolveUserOption = (value) => {
    if (!value) return null;
    const normalized = normalizeText(value);
    return (
      userOptions.find((option) => {
        const optionValue = normalizeText(option.value);
        const optionLabel = normalizeText(option.label);
        return optionValue === normalized || optionLabel === normalized;
      }) ?? null
    );
  };

  const resolveReasonOption = (value) => {
    if (!value) return null;
    const normalized = normalizeText(value);
    return (
      reasonOptions.find((option) => {
        const optionValue = normalizeText(option.value);
        const optionLabel = normalizeText(option.label);
        return optionValue === normalized || optionLabel === normalized;
      }) ?? null
    );
  };

  const resolveStatusOption = (value) => {
    if (!value) return null;
    const normalized = normalizeText(value);
    return (
      statusOptions.find((option) => {
        const optionValue = normalizeText(option.value);
        const optionLabel = normalizeText(option.label);
        return optionValue === normalized || optionLabel === normalized;
      }) ?? null
    );
  };

  const resolveTypeOption = (value) => {
    const normalizedValue = normalizeTypeValue(value);
    if (!normalizedValue) return null;
    return (
      typeOptions.find((option) => option.value === normalizedValue) ?? null
    );
  };

  // Find "Terminación de Contrato" reason from catalog
  const terminationReasonOption = useMemo(() => {
    return reasonOptions.find((opt) => {
      const label = normalizeText(opt.label);
      return label.includes('terminaci') || label.includes('termination') || label.includes('contrato');
    }) ?? null;
  }, [reasonOptions]);

  const handleTypeChange = (selected) => {
    const value = selected?.value ?? '';
    setForm((prev) => {
      const next = { ...prev, type: value };
      if (isPermanentType(value)) {
        // Permanent: lock status to Activa (2), reason to Terminación, start date to today
        const today = new Date().toISOString().split('T')[0];
        next.startDate = today;
        next.endDate = '';
        next.status = 2; // Activa
        if (terminationReasonOption) {
          next.reason = terminationReasonOption.value;
        }
      }
      return next;
    });
  };

  const isPermanent = isPermanentType(form.type);
  const today = new Date().toISOString().split('T')[0];
  const statusCode = Number(form.status) || null;
  const isScheduled = statusCode === 1;
  const isActive = statusCode === 2;
  const isFinished = statusCode === 3;

  // Temporal: exclude "Terminación de Contrato" from reasons
  const filteredReasonOptions = useMemo(() => {
    if (isPermanent) return reasonOptions;
    return reasonOptions.filter((opt) => {
      const label = normalizeText(opt.label);
      return !(label.includes('terminaci') || label.includes('termination') || label.includes('contrato'));
    });
  }, [reasonOptions, isPermanent]);

  const isValid = useMemo(() => {
    const hasUsers =
      form.fromUser && form.toUser && form.fromUser !== form.toUser;
    const hasDates = isPermanent ? true : form.startDate && form.endDate;
    const hasReason = form.reason;
    const hasType = Boolean(normalizeTypeValue(form.type));
    return hasUsers && hasDates && hasReason && hasType;
  }, [form, isPermanent]);

  const handleSave = async () => {
    if (!isValid || saving) return;

    // For permanent type: show confirmation modal first
    if (isPermanent) {
      const count = await fetchTaskCount(Number(form.fromUser));
      setPendingTaskCount(count);
      setShowPermanentConfirm(true);
      return;
    }

    await executeSave();
  };

  const handleConfirmPermanent = async () => {
    setShowPermanentConfirm(false);
    await executeSave();
  };

  const executeSave = async () => {
    if (saving) return;

    setSaving(true);
    const selectedReason = resolveReasonOption(form.reason);
    const reasonValue = selectedReason?.value ?? form.reason;
    const reasonId =
      reasonValue !== null &&
      reasonValue !== undefined &&
      reasonValue !== '' &&
      !Number.isNaN(Number(reasonValue))
        ? Number(reasonValue)
        : reasonValue;

    const typeValue = normalizeTypeValue(form.type);
    const permanent = isPermanentType(typeValue);

    const payload = {
      id: initialData?.id,
      outgoingUserId: Number(form.fromUser),
      substituteUserId: Number(form.toUser),
      transfers: null,
    };

    if (!permanent) {
      payload.startDate = form.startDate;
      payload.endDate = form.endDate;
    } else {
      payload.startDate = form.startDate || new Date().toISOString().split('T')[0];
    }

    if (typeValue) {
      payload.typeId = typeValue;
      payload.TypeId = typeValue;
    }

    if (reasonValue) {
      payload.reasonId = reasonId;
      payload.motivo = selectedReason?.label ?? form.reason;
    }

    const selectedStatus = resolveStatusOption(form.status);
    const statusValue =
      selectedStatus?.value ??
      form.status ??
      initialData?.estado ??
      initialData?.status ??
      defaultStatusId;

    if (
      statusValue !== null &&
      statusValue !== undefined &&
      statusValue !== ''
    ) {
      payload.statusId = Number(statusValue);
    }

    try {
      await onSave?.(payload);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  if (!Modal || !Input || !Dropdown) return null;

  const primaryLabel = isEdit
    ? t('delegations_modal_save_changes')
    : t('delegations_modal_save');

  return (
    <>
    <Modal
      title={
        isEdit
          ? t('delegations_modal_title_edit')
          : t('delegations_modal_title_create')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={primaryLabel}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || saving}
      secondaryBtnTxt={t('delegations_cancel')}
      secondaryBtnAction={onClose}
      width={760}
    >
      <div className='row g-3'>
        <div className='col-12 col-md-6'>
          {ComboBox ? (
            <ComboBox
              label={t('delegations_modal_from_user_label')}
              placeholder={t('delegations_modal_user_placeholder')}
              options={userOptions}
              value={resolveUserOption(form.fromUser)}
              onChange={(selected) =>
                setField('fromUser', selected?.value ?? '')
              }
              isClearable
            />
          ) : (
            <Dropdown
              label={t('delegations_modal_from_user_label')}
              placeholder={t('delegations_modal_user_placeholder')}
              options={userOptions}
              value={resolveUserOption(form.fromUser)}
              onChange={(selected) =>
                setField('fromUser', selected?.value ?? '')
              }
            />
          )}
        </div>

        <div className='col-12 col-md-6'>
          {ComboBox ? (
            <ComboBox
              label={t('delegations_modal_to_user_label')}
              placeholder={t('delegations_modal_user_placeholder')}
              options={userOptions}
              value={resolveUserOption(form.toUser)}
              onChange={(selected) => setField('toUser', selected?.value ?? '')}
              isClearable
            />
          ) : (
            <Dropdown
              label={t('delegations_modal_to_user_label')}
              placeholder={t('delegations_modal_user_placeholder')}
              options={userOptions}
              value={resolveUserOption(form.toUser)}
              onChange={(selected) => setField('toUser', selected?.value ?? '')}
            />
          )}
        </div>

        {/* Type + Reason row */}
        <div className='col-12 col-md-6'>
          <Dropdown
            label={t('delegations_modal_type_label')}
            placeholder={t('delegations_modal_type_placeholder')}
            options={typeOptions}
            value={resolveTypeOption(form.type)}
            onChange={handleTypeChange}
          />
        </div>
        <div className='col-12 col-md-6'>
          <Dropdown
            label={t('delegations_modal_reason_label')}
            placeholder={t('delegations_modal_reason_placeholder')}
            options={filteredReasonOptions}
            value={isPermanent && terminationReasonOption ? terminationReasonOption : resolveReasonOption(form.reason)}
            onChange={(selected) => setField('reason', selected?.value ?? '')}
            disabled={isPermanent}
          />
        </div>

        {/* Date row */}
        <div className='col-12 col-md-6'>
          <Input
            label={t('delegations_modal_start_date_label')}
            placeholder={t('delegations_modal_date_placeholder')}
            type='date'
            value={form.startDate}
            onChange={(event) => setField('startDate', event.target.value)}
            disabled={isPermanent}
            min={!isPermanent && isScheduled ? today : undefined}
            max={!isPermanent && isActive ? today : undefined}
            helperText={
              !isPermanent && isScheduled ? t('delegations_helper_scheduled_dates') :
              !isPermanent && isActive ? t('delegations_helper_active_start_date') : undefined
            }
            error={
              (!isPermanent && isScheduled && form.startDate && form.startDate < today) ? t('delegations_helper_scheduled_dates') :
              (!isPermanent && isActive && form.startDate && form.startDate > today) ? t('delegations_helper_active_start_date') : undefined
            }
          />
        </div>
        <div className='col-12 col-md-6'>
          {!isPermanent ? (
            <Input
              label={t('delegations_modal_end_date_label')}
              placeholder={t('delegations_modal_date_placeholder')}
              type='date'
              value={form.endDate}
              onChange={(event) => setField('endDate', event.target.value)}
              min={isScheduled ? today : isActive ? today : (form.startDate || undefined)}
              max={isFinished ? today : undefined}
              helperText={
                isScheduled ? t('delegations_helper_scheduled_dates') :
                isActive ? t('delegations_helper_active_end_date') :
                isFinished ? t('delegations_helper_finished_end_date') : undefined
              }
              error={
                (isScheduled && form.endDate && form.endDate < today) ? t('delegations_helper_scheduled_dates') :
                (isActive && form.endDate && form.endDate < today) ? t('delegations_helper_active_end_date') :
                (isFinished && form.endDate && form.endDate > today) ? t('delegations_helper_finished_end_date') : undefined
              }
            />
          ) : (
            <>{/* Empty placeholder to keep grid alignment */}</>
          )}
        </div>

        <div className='col-12 col-md-6'>
          <Dropdown
            label={t('delegations_modal_status_label')}
            placeholder={t('delegations_modal_status_placeholder')}
            options={statusOptions}
            value={isPermanent ? resolveStatusOption(2) : resolveStatusOption(form.status)}
            onChange={(selected) => setField('status', selected?.value ?? '')}
            disabled={isPermanent}
          />
        </div>

        {/* <div className='col-12'>
          <div className='border rounded p-3'>
            <p className='mb-3'>¿Quiere transferir al usuario suplente?</p>
            <div className='d-flex flex-column gap-2'>
              {Checkbox ? (
                <>
                  <Checkbox
                    id='delegation-transfer-cases'
                    label='Transferir casos asignados'
                    checked={form.transfers.cases}
                    onChange={(e) => setTransfer('cases', e.target.checked)}
                  />
                  <Checkbox
                    id='delegation-transfer-tasks'
                    label='Transferir tareas pendientes'
                    checked={form.transfers.tasks}
                    onChange={(e) => setTransfer('tasks', e.target.checked)}
                  />
                  <Checkbox
                    id='delegation-transfer-events'
                    label='Transferir eventos de agenda'
                    checked={form.transfers.events}
                    onChange={(e) => setTransfer('events', e.target.checked)}
                  />
                </>
              ) : (
                <>
                  <label className='d-flex align-items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={form.transfers.cases}
                      onChange={(e) => setTransfer('cases', e.target.checked)}
                    />
                    Transferir Casos Asignados
                  </label>
                  <label className='d-flex align-items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={form.transfers.tasks}
                      onChange={(e) => setTransfer('tasks', e.target.checked)}
                    />
                    Transferir Tareas Pendientes
                  </label>
                  <label className='d-flex align-items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={form.transfers.events}
                      onChange={(e) => setTransfer('events', e.target.checked)}
                    />
                    Transferir Eventos de Agenda
                  </label>
                </>
              )}
            </div>
          </div>
        </div> */}
      </div>
    </Modal>
    {showPermanentConfirm && (
      <ConfirmModal
        Modal={Modal}
        show={showPermanentConfirm}
        title={t('delegations_permanent_confirm_title')}
        message={t('delegations_permanent_confirm_message', {
          fromUser: resolveUserOption(form.fromUser)?.label ?? form.fromUser,
          toUser: resolveUserOption(form.toUser)?.label ?? form.toUser,
          taskCount: pendingTaskCount,
        })}
        confirmLabel={t('delegations_permanent_confirm_button')}
        cancelLabel={t('delegations_cancel')}
        confirmVariant='danger'
        onConfirm={handleConfirmPermanent}
        onClose={() => setShowPermanentConfirm(false)}
      />
    )}
    </>
  );
}
